import React, { useState, useEffect } from 'react';
import {
  getAllPosts,
  getAllCategories,
  getAllComments,
  getBlogStats,
  deletePost,
  deleteCategory,
  approveComment,
  deleteComment,
  createCategory,
  BlogPost,
  BlogCategory
} from '../../services/blogService';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import './BlogManager.scss';
import BlogPostEditor from './BlogPostEditor';

enum Tab {
  Dashboard = 'dashboard',
  Posts = 'posts',
  Categories = 'categories',
  Comments = 'comments',
  NewPost = 'new-post',
  EditPost = 'edit-post'
}

interface BlogStats {
  postCount: number;
  publishedCount: number;
  commentCount: number;
  approvedCommentCount: number;
  categoryCount: number;
  tagCount: number;
}

const BlogManager: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>(Tab.Dashboard);
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [comments, setComments] = useState<any[]>([]);
  const [stats, setStats] = useState<BlogStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  const [showNewCategoryModal, setShowNewCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const postsPerPage = 10;

  // Fetch data based on active tab
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      
      try {
        switch (activeTab) {
          case Tab.Dashboard:
            const blogStats = await getBlogStats();
            setStats(blogStats);
            break;
          
          case Tab.Posts:
            const { data: postsData, count: postsCount } = await getAllPosts(currentPage, postsPerPage);
            setPosts(postsData);
            setTotalPages(Math.ceil((postsCount || 0) / postsPerPage));
            break;
          
          case Tab.Categories:
            const categoriesData = await getAllCategories();
            setCategories(categoriesData);
            break;
          
          case Tab.Comments:
            const { data: commentsData, count: commentsCount } = await getAllComments(currentPage, 20);
            setComments(commentsData);
            setTotalPages(Math.ceil((commentsCount || 0) / 20));
            break;
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to fetch data. Please try again.');
      }
      
      setLoading(false);
    };
    
    if (activeTab !== Tab.NewPost && activeTab !== Tab.EditPost) {
      fetchData();
    }
  }, [activeTab, currentPage]);

  // Handle post deletion
  const handleDeletePost = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this post? This action cannot be undone.')) {
      try {
        const result = await deletePost(id);
        
        if (result) {
          // Refresh posts list
          const { data } = await getAllPosts(currentPage, postsPerPage);
          setPosts(data);
          setSuccess('Post deleted successfully');
          
          // Auto-hide success message after 3 seconds
          setTimeout(() => setSuccess(null), 3000);
        } else {
          setError('Failed to delete post');
        }
      } catch (err) {
        console.error('Error deleting post:', err);
        setError('An error occurred while deleting the post');
      }
    }
  };

  // Handle category deletion
  const handleDeleteCategory = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this category? Posts in this category will not be deleted but will no longer be categorized.')) {
      try {
        const result = await deleteCategory(id);
        
        if (result) {
          // Refresh categories list
          const categoriesData = await getAllCategories();
          setCategories(categoriesData);
          setSuccess('Category deleted successfully');
          
          // Auto-hide success message after 3 seconds
          setTimeout(() => setSuccess(null), 3000);
        } else {
          setError('Failed to delete category');
        }
      } catch (err) {
        console.error('Error deleting category:', err);
        setError('An error occurred while deleting the category');
      }
    }
  };

  // Handle comment approval
  const handleApproveComment = async (id: string) => {
    try {
      const result = await approveComment(id);
      
      if (result) {
        // Refresh comments list
        const { data } = await getAllComments(currentPage, 20);
        setComments(data);
        setSuccess('Comment approved successfully');
        
        // Auto-hide success message after 3 seconds
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Failed to approve comment');
      }
    } catch (err) {
      console.error('Error approving comment:', err);
      setError('An error occurred while approving the comment');
    }
  };

  // Handle comment deletion
  const handleDeleteComment = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this comment?')) {
      try {
        const result = await deleteComment(id);
        
        if (result) {
          // Refresh comments list
          const { data } = await getAllComments(currentPage, 20);
          setComments(data);
          setSuccess('Comment deleted successfully');
          
          // Auto-hide success message after 3 seconds
          setTimeout(() => setSuccess(null), 3000);
        } else {
          setError('Failed to delete comment');
        }
      } catch (err) {
        console.error('Error deleting comment:', err);
        setError('An error occurred while deleting the comment');
      }
    }
  };

  // Handle new category creation
  const handleCreateCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newCategoryName.trim()) {
      setError('Category name is required');
      return;
    }
    
    setIsCreatingCategory(true);
    setError(null);
    
    try {
      const result = await createCategory(newCategoryName, newCategoryDescription);
      
      if (result) {
        // Refresh categories list
        const categoriesData = await getAllCategories();
        setCategories(categoriesData);
        setSuccess('Category created successfully');
        
        // Reset form and close modal
        setNewCategoryName('');
        setNewCategoryDescription('');
        setShowNewCategoryModal(false);
        
        // Auto-hide success message after 3 seconds
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Failed to create category');
      }
    } catch (err) {
      console.error('Error creating category:', err);
      setError('An error occurred while creating the category');
    }
    
    setIsCreatingCategory(false);
  };

  // Format date for display
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Truncate text to specified length
  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substr(0, maxLength) + '...';
  };

  // Strip HTML tags from text
  const stripHtml = (html: string) => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // Get status badge class
  const getStatusBadgeClass = (published?: boolean) => {
    return published ? 'badge-success' : 'badge-warning';
  };

  // Navigate between pages
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle tab change
  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setError(null);
    setSuccess(null);
  };

  // Handle edit post
  const handleEditPost = (post: BlogPost) => {
    setSelectedPost(post);
    setActiveTab(Tab.EditPost);
  };

  // Render the dashboard tab
  const renderDashboard = () => {
    if (!stats) return <div className="loading">Loading stats...</div>;
    
    return (
      <div className="blog-dashboard">
        <h2>Blog Dashboard</h2>
        
        <div className="stats-cards">
          <div className="stats-card">
            <div className="stats-card-content">
              <span className="stats-card-value">{stats.postCount}</span>
              <span className="stats-card-label">Total Posts</span>
            </div>
            <div className="stats-card-icon">
              <i className="fas fa-file-alt"></i>
            </div>
          </div>
          
          <div className="stats-card">
            <div className="stats-card-content">
              <span className="stats-card-value">{stats.publishedCount}</span>
              <span className="stats-card-label">Published Posts</span>
            </div>
            <div className="stats-card-icon">
              <i className="fas fa-check-circle"></i>
            </div>
          </div>
          
          <div className="stats-card">
            <div className="stats-card-content">
              <span className="stats-card-value">{stats.categoryCount}</span>
              <span className="stats-card-label">Categories</span>
            </div>
            <div className="stats-card-icon">
              <i className="fas fa-folder"></i>
            </div>
          </div>
          
          <div className="stats-card">
            <div className="stats-card-content">
              <span className="stats-card-value">{stats.tagCount}</span>
              <span className="stats-card-label">Tags</span>
            </div>
            <div className="stats-card-icon">
              <i className="fas fa-tags"></i>
            </div>
          </div>
          
          <div className="stats-card">
            <div className="stats-card-content">
              <span className="stats-card-value">{stats.commentCount}</span>
              <span className="stats-card-label">Comments</span>
            </div>
            <div className="stats-card-icon">
              <i className="fas fa-comments"></i>
            </div>
          </div>
          
          <div className="stats-card">
            <div className="stats-card-content">
              <span className="stats-card-value">{stats.approvedCommentCount}</span>
              <span className="stats-card-label">Approved Comments</span>
            </div>
            <div className="stats-card-icon">
              <i className="fas fa-comment-check"></i>
            </div>
          </div>
        </div>
        
        <div className="dashboard-actions">
          <button 
            className="action-button"
            onClick={() => handleTabChange(Tab.NewPost)}
          >
            <i className="fas fa-plus"></i>
            Create New Post
          </button>
          
          <button 
            className="action-button"
            onClick={() => handleTabChange(Tab.Posts)}
          >
            <i className="fas fa-list"></i>
            Manage Posts
          </button>
          
          <button 
            className="action-button"
            onClick={() => {
              setShowNewCategoryModal(true);
            }}
          >
            <i className="fas fa-folder-plus"></i>
            Add Category
          </button>
          
          <button 
            className="action-button"
            onClick={() => handleTabChange(Tab.Comments)}
          >
            <i className="fas fa-comments"></i>
            Moderate Comments
          </button>
        </div>
        
        <div className="dashboard-quick-links">
          <h3>Quick Links</h3>
          <div className="quick-links">
            <Link to="/blog" className="quick-link" target="_blank">
              <i className="fas fa-external-link-alt"></i>
              View Blog
            </Link>
            
            {stats.commentCount > stats.approvedCommentCount && (
              <button 
                className="quick-link"
                onClick={() => handleTabChange(Tab.Comments)}
              >
                <i className="fas fa-comment-dots"></i>
                {stats.commentCount - stats.approvedCommentCount} comments awaiting approval
              </button>
            )}
            
            {stats.postCount > stats.publishedCount && (
              <button 
                className="quick-link"
                onClick={() => handleTabChange(Tab.Posts)}
              >
                <i className="fas fa-file-alt"></i>
                {stats.postCount - stats.publishedCount} draft posts
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render the posts tab
  const renderPosts = () => {
    return (
      <div className="blog-posts-manager">
        <div className="section-header">
          <h2>Manage Posts</h2>
          <button 
            className="new-post-button"
            onClick={() => handleTabChange(Tab.NewPost)}
          >
            <i className="fas fa-plus"></i>
            New Post
          </button>
        </div>
        
        {loading ? (
          <div className="loading">Loading posts...</div>
        ) : posts.length === 0 ? (
          <div className="no-posts">
            <p>No posts found.</p>
            <button 
              className="new-post-button"
              onClick={() => handleTabChange(Tab.NewPost)}
            >
              Create your first post
            </button>
          </div>
        ) : (
          <>
            <div className="posts-table-container">
              <table className="posts-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Category</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {posts.map(post => (
                    <tr key={post.id}>
                      <td>
                        <div className="post-title-cell">
                          <span className="post-title">{truncateText(post.title, 50)}</span>
                          <span className="post-excerpt">{truncateText(stripHtml(post.summary), 70)}</span>
                        </div>
                      </td>
                      <td>{post.category?.name || 'Uncategorized'}</td>
                      <td>
                        <span className={`status-badge ${getStatusBadgeClass(post.published)}`}>
                          {post.published ? 'Published' : 'Draft'}
                        </span>
                      </td>
                      <td>
                        {post.published 
                          ? formatDate(post.published_at) 
                          : formatDate(post.created_at)}
                      </td>
                      <td>
                        <div className="post-actions">
                          <button 
                            className="action-button edit"
                            onClick={() => handleEditPost(post)}
                            title="Edit post"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          
                          <a 
                            href={`/blog/${post.slug}`}
                            className="action-button view"
                            target="_blank"
                            rel="noopener noreferrer"
                            title="View post"
                          >
                            <i className="fas fa-eye"></i>
                          </a>
                          
                          <button 
                            className="action-button delete"
                            onClick={() => handleDeletePost(post.id)}
                            title="Delete post"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {totalPages > 1 && (
              <div className="pagination">
                <button 
                  onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                  disabled={currentPage === 1}
                  className="pagination-button prev"
                >
                  <i className="fas fa-chevron-left"></i>
                  Previous
                </button>
                
                <div className="page-numbers">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`page-number ${currentPage === page ? 'active' : ''}`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                
                <button 
                  onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="pagination-button next"
                >
                  Next
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // Render the categories tab
  const renderCategories = () => {
    return (
      <div className="blog-categories-manager">
        <div className="section-header">
          <h2>Manage Categories</h2>
          <button 
            className="new-category-button"
            onClick={() => setShowNewCategoryModal(true)}
          >
            <i className="fas fa-plus"></i>
            New Category
          </button>
        </div>
        
        {loading ? (
          <div className="loading">Loading categories...</div>
        ) : categories.length === 0 ? (
          <div className="no-categories">
            <p>No categories found.</p>
            <button 
              className="new-category-button"
              onClick={() => setShowNewCategoryModal(true)}
            >
              Create your first category
            </button>
          </div>
        ) : (
          <div className="categories-table-container">
            <table className="categories-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Slug</th>
                  <th>Posts</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.map(category => (
                  <tr key={category.id}>
                    <td>{category.name}</td>
                    <td>{category.description || '-'}</td>
                    <td>{category.slug}</td>
                    <td>{category.post_count || 0}</td>
                    <td>
                      <div className="category-actions">
                        <a 
                          href={`/blog/category/${category.slug}`}
                          className="action-button view"
                          target="_blank"
                          rel="noopener noreferrer"
                          title="View category"
                        >
                          <i className="fas fa-eye"></i>
                        </a>
                        
                        <button 
                          className="action-button delete"
                          onClick={() => handleDeleteCategory(category.id)}
                          title="Delete category"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // Render the comments tab
  const renderComments = () => {
    return (
      <div className="blog-comments-manager">
        <div className="section-header">
          <h2>Manage Comments</h2>
        </div>
        
        {loading ? (
          <div className="loading">Loading comments...</div>
        ) : comments.length === 0 ? (
          <div className="no-comments">
            <p>No comments found.</p>
          </div>
        ) : (
          <>
            <div className="comments-table-container">
              <table className="comments-table">
                <thead>
                  <tr>
                    <th>Author</th>
                    <th>Comment</th>
                    <th>Post</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {comments.map(comment => (
                    <tr key={comment.id} className={comment.approved ? '' : 'unapproved'}>
                      <td>
                        {comment.user 
                          ? `${comment.user.first_name} ${comment.user.last_name}`
                          : `${comment.guest_name} (Guest)`}
                      </td>
                      <td>
                        <div className="comment-content">
                          {truncateText(comment.content, 100)}
                        </div>
                      </td>
                      <td>
                        <a 
                          href={`/blog/${comment.post.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {truncateText(comment.post.title, 30)}
                        </a>
                      </td>
                      <td>{formatDate(comment.created_at)}</td>
                      <td>
                        <span className={`status-badge ${comment.approved ? 'badge-success' : 'badge-warning'}`}>
                          {comment.approved ? 'Approved' : 'Pending'}
                        </span>
                      </td>
                      <td>
                        <div className="comment-actions">
                          {!comment.approved && (
                            <button 
                              className="action-button approve"
                              onClick={() => handleApproveComment(comment.id)}
                              title="Approve comment"
                            >
                              <i className="fas fa-check"></i>
                            </button>
                          )}
                          
                          <button 
                            className="action-button delete"
                            onClick={() => handleDeleteComment(comment.id)}
                            title="Delete comment"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {totalPages > 1 && (
              <div className="pagination">
                <button 
                  onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                  disabled={currentPage === 1}
                  className="pagination-button prev"
                >
                  <i className="fas fa-chevron-left"></i>
                  Previous
                </button>
                
                <div className="page-numbers">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`page-number ${currentPage === page ? 'active' : ''}`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                
                <button 
                  onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="pagination-button next"
                >
                  Next
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // Render new/edit post tab
  const renderPostEditor = () => {
    return (
      <BlogPostEditor
        post={activeTab === Tab.EditPost ? selectedPost : null}
        onCancel={() => {
          setActiveTab(Tab.Posts);
          setSelectedPost(null);
        }}
        onSave={() => {
          setActiveTab(Tab.Posts);
          setSelectedPost(null);
        }}
      />
    );
  };

  // New Category Modal
  const renderNewCategoryModal = () => {
    if (!showNewCategoryModal) return null;
    
    return (
      <div className="modal-overlay">
        <div className="modal">
          <div className="modal-header">
            <h3>Add New Category</h3>
            <button
              className="close-button"
              onClick={() => setShowNewCategoryModal(false)}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
          
          <div className="modal-body">
            <form onSubmit={handleCreateCategory}>
              <div className="form-group">
                <label htmlFor="category-name">Category Name</label>
                <input
                  id="category-name"
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="Enter category name"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="category-description">Description (optional)</label>
                <textarea
                  id="category-description"
                  value={newCategoryDescription}
                  onChange={(e) => setNewCategoryDescription(e.target.value)}
                  placeholder="Enter category description"
                  rows={3}
                ></textarea>
              </div>
              
              {error && <div className="error-message">{error}</div>}
              
              <div className="form-buttons">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={() => setShowNewCategoryModal(false)}
                >
                  Cancel
                </button>
                
                <button
                  type="submit"
                  className="submit-button"
                  disabled={isCreatingCategory}
                >
                  {isCreatingCategory ? 'Creating...' : 'Create Category'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="blog-manager">
      <div className="blog-manager-header">
        <h1>Blog Manager</h1>
      </div>
      
      <div className="blog-manager-tabs">
        <div className="tabs-list">
          <button
            className={`tab ${activeTab === Tab.Dashboard ? 'active' : ''}`}
            onClick={() => handleTabChange(Tab.Dashboard)}
          >
            <i className="fas fa-tachometer-alt"></i>
            Dashboard
          </button>
          
          <button
            className={`tab ${activeTab === Tab.Posts ? 'active' : ''}`}
            onClick={() => handleTabChange(Tab.Posts)}
          >
            <i className="fas fa-file-alt"></i>
            Posts
          </button>
          
          <button
            className={`tab ${activeTab === Tab.Categories ? 'active' : ''}`}
            onClick={() => handleTabChange(Tab.Categories)}
          >
            <i className="fas fa-folder"></i>
            Categories
          </button>
          
          <button
            className={`tab ${activeTab === Tab.Comments ? 'active' : ''}`}
            onClick={() => handleTabChange(Tab.Comments)}
          >
            <i className="fas fa-comments"></i>
            Comments
          </button>
        </div>
      </div>
      
      {error && (
        <div className="alert alert-error">
          <i className="fas fa-exclamation-circle"></i>
          {error}
          <button 
            className="close-button"
            onClick={() => setError(null)}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}
      
      {success && (
        <div className="alert alert-success">
          <i className="fas fa-check-circle"></i>
          {success}
          <button 
            className="close-button"
            onClick={() => setSuccess(null)}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}
      
      <div className="tab-content">
        {activeTab === Tab.Dashboard && renderDashboard()}
        {activeTab === Tab.Posts && renderPosts()}
        {activeTab === Tab.Categories && renderCategories()}
        {activeTab === Tab.Comments && renderComments()}
        {(activeTab === Tab.NewPost || activeTab === Tab.EditPost) && renderPostEditor()}
      </div>
      
      {renderNewCategoryModal()}
    </div>
  );
};

export default BlogManager; 